<template>
<!-- eslint-disable max-len -->
<span>
  <transition enter-active-class="animated slideInDown" leave-active-class="animated fadeOut">
    <div class="report-container" id="report-container">
      <div class="report" ref="report" id="report">
        <!-- logo -->
        <img :src="resolveAssetPath(activeEnvironment.reportLogo || activeEnvironment.creditsLogo || activeEnvironment.titleLogo, 'ui/customer')" class="logo" ref="logo">

        <!-- title -->
        <h1 class="report-header report-title">Entrepreneurship Readiness Report</h1>

        <!-- Student/Date -->
        <div class="labelbox accent-color">
          <div class="labelbox-flexcontainer labelbox-flexcontainer-margin">
            <div class="report-header label">
            Student:
            </div>
            <div class="report-header label">
            Date:
            </div>
          </div>
          <div class="valuebox">
            <div class="valuebox-flexcontainer">
              <div class="value-item">
              {{ username }}
              </div>
              <div class="value-item" style="text-align: right;">
              {{ date }}
              </div>
            </div>
          </div>
        </div>

        <!-- Lit Score/Metrics/Revenue -->
        <div class="labelbox accent-color extra-top-margin">
          <div class="labelbox-flexcontainer labelbox-flexcontainer-margin">
            <div class="report-header label">
              Literature Score:
              <img :src="resolveAssetPath('litscore-icon.png', 'ui/customer')" class="label-icon" style="left: -42.56px; width: 35.84px;">
            </div>
            <div class="report-header label" style="margin-right: 140px;">
              Metrics:
              <img :src="resolveAssetPath('metrics-icon.png', 'ui/customer')" class="label-icon" style="left: -44.8px; width: 33.6px;">
            </div>
            <div class="report-header label">
              Revenue:
              <img :src="resolveAssetPath('revenue-icon.png', 'ui/customer')" class="label-icon" style="left: -71.68px; width: 56px;">
            </div>
          </div>
          <div class="valuebox">
            <div class="valuebox-flexcontainer">
              <div class="value-item">
                {{ literatureScore }}%
              </div>
              <div class="valuebox-flexcontainer metrics-values">
                <div class="metric-flexcontainer">
                  <div class="metric-text-label">
                  Customer:
                  </div>
                  <div class="metric-text-value">
                    {{ customerMetric }}%
                  </div>
                </div>
                <div class="metric-flexcontainer">
                  <div class="metric-text-label">
                  Employee:
                  </div>
                  <div class="metric-text-value">
                    {{ employeeMetric }}%
                  </div>
                </div>
                <div class="metric-flexcontainer">
                  <div class="metric-text-label">
                  Investor:
                  </div>
                  <div class="metric-text-value">
                    {{ shareholderMetric }}%
                  </div>
                </div>
              </div>
              <div class="value-item" style="text-align: right;">
                ${{ revenue.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Purple HERO Results Bar -->
        <div class="report-flex">
          <h2 class="report-header hero-title">
            Psychological Capital
          </h2>
          <radar
            class="hero-graph"
            :stats="stats"
            :polycolor="polycolor"
            :radar="radar"
            :gridlineCount="2"
            gridlineColor="white"
            axesColor="white"
            :fillColor="accentColor"
            :halfwayFillColor="accentColor"
            :borderWidth="10"
            borderColor="#b7a57a"
            extraBorder
            showVertices
            :labelSize="75"
            :startingAngleRadians="0"
            :maxValue="maxStatValue"
          ></radar>
          <div class="hero-values">
            <div class="hero-value">
              <div class="hero-value-label">
                H
              </div>
              <div class="hero-value-number">
                {{ stats[0].value }}
              </div>
            </div>
            <div class="hero-value">
              <div class="hero-value-label">
                E
              </div>
              <div class="hero-value-number">
                {{ stats[1].value }}
              </div>
            </div>
            <div class="hero-value">
              <div class="hero-value-label">
                R
              </div>
              <div class="hero-value-number">
                {{ stats[2].value }}
              </div>
            </div>
            <div class="hero-value">
              <div class="hero-value-label">
                O
              </div>
              <div class="hero-value-number">
                {{ stats[3].value }}
              </div>
            </div>
          </div>
        </div>

        <!-- Static HERO Tips -->
        <div class="hero-tip hero-tip-extra-margin" style="height: 156px;">
          <div class="hero-tip-flex-container" style="top: -28.8px;">
            <div class="hero-tip-definition-box accent-color" style="height: 204px;">
              <div class="hero-tip-label">
                Hope
              </div>
              <div class="hero-tip-definition">
                A positive approach to goals characterized by strong desire, determination and ingenuity toward goal fulfillment.
              </div>
            </div>
            <div class="hero-tip-details-flex-container" style="height: 172px;">
              <div class="hero-tip-details">
                Project planning, goal-seeking and risk management activities can increase Hope.  Taking the time to intentionally plot out alternative pathways, risk mitigation strategies and contingency plans can help individuals prepare for potential roadblocks.
              </div>
              <div class="hero-tip-details">
                Research has shown a link between Hope and humor.  Humor can be a buffer for stress.  Finding the irony or humor in a situation requires a shift in thinking and can result in positive reappraisal of a difficult situation.
                <br/>
                The positive reappraisal can result in subsequent positive results.  In short, humor can open up different and more positive thinking that helps us see a way around obstacles.  Humor also creates biological responses (e.g., smiles and the release of endorphins) that can boost energy and relieve stress.  So, literally "learning to laugh in the face of adversity" can improve hopeful thinking.
              </div>
            </div>
          </div>
          <div class="hero-tip-subtitle" style="top: -18.4px;">
            Tips for building Hope
          </div>
        </div>
        <div class="hero-tip hero-tip-margin" style="height: 184px;">
          <div class="hero-tip-flex-container" style="top: -28.8px;">
            <div class="hero-tip-definition-box accent-color" style="height: 232px;">
              <div class="hero-tip-label">
                Efficacy
              </div>
              <div class="hero-tip-definition">
                Confidence to take on and putting necessary effort to succeed at challenging tasks.
              </div>
            </div>
            <div class="hero-tip-details-flex-container" style="height: 200px;">
              <div class="hero-tip-details">
                You can develop Efficacy though self-awareness and reflection, by looking back on your past actions, successes, and failures, and then creating plans that you believe will succeed.  This is an iterative process that builds upon itself, as you continually gain experience that will better inform your future plans.
              </div>
              <div class="hero-tip-details">
                Recognize that Efficacy beliefs are related to goals.  For instance, unrealistically high goals can lead to lower Efficacy beliefs.  Reasonable and moderately challenging goals can lead to success and higher Efficacy for similar tasks and challenges.  You can control your Efficacy beliefs by setting appropriate goals.
              </div>
              <div class="hero-tip-details">
                Try to build a history of incremental success.  Put yourself in situations where success is highly likely and then gradually increase the difficulty or complexity of the progressive challenges you take on.
              </div>
              <div class="hero-tip-details">
                Don't focus on failures or allow failures to make you afraid of trying again.  Use positive self-talk to assure yourself that it takes practice to improve.  Setbacks and learning from mistakes are part of practice and improvement.
              </div>
            </div>
          </div>
          <div class="hero-tip-subtitle" style="top: -18.4px;">
            Tips for building Efficacy
          </div>
        </div>
        <div class="hero-tip hero-tip-margin" style="height: 118px;">
          <div class="hero-tip-flex-container" style="top: -28.8px;">
            <div class="hero-tip-definition-box accent-color" style="height: 166px;">
              <div class="hero-tip-label">
                Resiliency
              </div>
              <div class="hero-tip-definition">
                Sustaining and bouncing back (and beyond) to attain success when beset by problems and adversity.
              </div>
            </div>
            <div class="hero-tip-details-flex-container" style="height: 134px;">
              <div class="hero-tip-details">
                Emotional Resiliency has a physical connection.  Intentionally invest in your own physical well-being through a healthy diet, exercise and adequate rest and sleep—especially when change is on the horizon.
              </div>
              <div class="hero-tip-details">
                Establish clear priorities so that you can engage in a balance of activities that are stressful or draining, but necessary.
              </div>
              <div class="hero-tip-details">
                Get organized so that you know and can proactively manage priorities.  Time management, to-do lists and reminder systems can help conserve emotional resources during adverse, stressful or changing situations.
              </div>
            </div>
          </div>
          <div class="hero-tip-subtitle" style="top: -18.4px;">
            Tips for building Resiliency
          </div>
        </div>
        <div class="hero-tip hero-tip-margin" style="height: 122px;">
          <div class="hero-tip-flex-container" style="top: -28.8px;">
            <div class="hero-tip-definition-box accent-color" style="height: 170px;">
              <div class="hero-tip-label">
                Optimism
              </div>
              <div class="hero-tip-definition">
                Making positive attributions about succeeding now and in the future.
              </div>
            </div>
            <div class="hero-tip-details-flex-container" style="height: 138px;">
              <div class="hero-tip-details">
                Reflect on your conscious thought processes and imagine how they would sound being spoken by another person whose goal in life is to make you unhappy.
              </div>
              <div class="hero-tip-details">
                This process can help you reject negative "self-talk" as unproductive and unacceptable.  Allow yourself to distribute pessimistic thoughts or beliefs and check out their accuracy.  When we dispute, we use the same techniques which we use to encourage others to validate their assumptions and consider alternative possibilities.
              </div>
              <div class="hero-tip-details">
                Distract yourself from pessimistic thoughts by doing something you enjoy or talking to someone who is feeling more optimistic.  Realize when you need help reframing a situation and ask others to help you see other potential realities.
              </div>
            </div>
          </div>
          <div class="hero-tip-subtitle" style="top: -18.4px;">
            Tips for building Optimism
          </div>
        </div>
        <!-- footer -->
        <div
          v-if="pdf && (activeEnvironment.reportFooter || activeEnvironment.reportFooterLogo)"
          style="
            background-color: black;
            color: white;
            padding: 4px 50px;
            width: 100%;
            min-height: 36px;
            display: flex;
            flex-flow: row nowrap;
            justify-content: space-between;
            align-items: flex-start;
          "
        >
          <!-- footer message (e.g. displaying ©) -->
          <div v-html="activeEnvironment.reportFooter"></div>
          <!-- footer logo -->
          <img
            v-if="activeEnvironment.reportFooterLogo"
            :src="resolveAssetPath(activeEnvironment.reportFooterLogo, 'ui/customer')"
            :class="`footer-logo ${pdf ? 'pdf' : ''}`"
            ref="footerLogo"
          >
        </div>
      </div>
    </div>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <button v-if="showNextButton"
      class="btn download-button"
      @click.prevent="onClickDownload"
    >Download</button>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <bottom-bar v-if="showNextButton" :next="showNarratorNotification ? onComplete : undefined" :tabindex="3" ref="bottomBar" :noBar="true">
      <span slot="button">
        <span v-html="(narrative && narrative.nextButtonText) ? narrative.nextButtonText : ((game && game.nextButtonDefaultText) ? game.nextButtonDefaultText : 'Next')"></span>
      </span>
    </bottom-bar>
  </transition>
</span>
</template>

<script>
/* eslint-disable max-len */
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import JsPDF from 'jspdf';
import domtoimage from 'dom-to-image-more';

import TopBar from '../ui/TopBar';
import MetricsNavbar from '../ui/MetricsNavbar';
import CashNavbar from '../ui/CashNavbar';
import Narrator from '../ui/Narrator';
import NarratorNotification from '../ui/NarratorNotification';
import BottomBar from '../ui/BottomBar';
import Radar from '../ui/Radar';

export default {
  // $emit 'input' on complete
  components: {
    TopBar,
    MetricsNavbar,
    CashNavbar,
    Narrator,
    NarratorNotification,
    BottomBar,
    Radar
  },
  data() {
    return {
      showNextButton: false,
      showNarrator: false,
      showNarratorNotification: false,
      pdf: false,
      radar: {
        size: '370',
        viewbox: '1200',
        radius: '340'
      },
      polycolor: 'rgba(0, 51, 142, 0.5)',
      accentColor: '#D9CDA8',
      maxStatValue: 15,
      stats: [
        {
          name: 'H',
          value: 0,
        },
        {
          name: 'E',
          value: 0,
        },
        {
          name: 'R',
          value: 0,
        },
        {
          name: 'O',
          value: 0,
        }
      ],
      literatureScore: 0,
      customerMetric: 0,
      employeeMetric: 0,
      shareholderMetric: 0,
      revenue: 0
    };
  },
  computed: {
    ...mapGetters([
      'characters',
      'tooltip',
      'narrative',
      'lastNarrative',
      'nextNarrative',
      'narrativeNavbarText',
      'narrativeText',
      'group',
      'me',
      'metrics',
      'game',
      'dateActiveGameCompleted',
      'activeEnvironment'
    ]),
    username() {
      const {
        firstName = '',
        lastName = '',
        email = ''
      } = this.me || {};
      return firstName || lastName ? `${firstName} ${lastName}` : email;
    },
    date() {
      return (this.game.carryOverMetricsFromActive && this.dateActiveGameCompleted != null)
        ? new Date(this.dateActiveGameCompleted).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        : new Date(Date.now()).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
  },
  // async created() {
  //   await this.loadPageData();
  //   this.setStats();
  // },
  methods: {
    ...mapActions([
      'stopSounds',
      'getMyGroup'
    ]),
    async show() {
      this.showNarrator = true;
      await Vue.nextTick();
      this.stopSounds();
      await new Promise(resolve => setTimeout(resolve, 1250));
      this.showNarratorNotification = true;
      await new Promise(resolve => setTimeout(resolve, 1250));
      await Vue.nextTick();
    },
    async reset() {
      await Vue.nextTick();
      await this.show();
    },
    navigate() {
      this.$refs.bottomBar.navigate();
    },
    async onComplete() {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        window.dispatchEvent(new Event('click'));
        return;
      }
      this.$emit('input');
    },
    async loadPageData() {
      try {
        const { _id: groupId } = this.group;
        await this.getMyGroup({ groupId });
      } catch (err) {
        if (await this.$router.handleGameError(err)) return;
        this.$router.showGameErrorCritical();
      }
    },
    setStats() {
      const mLength = Math.min(this.stats.length, this.group.players[0].metrics.length);
      this.maxStatValue = 15; // minimum max of 15
      for (let i = 0; i < mLength; i++) {
        this.stats[i].value = this.group.players[0].metrics[i].value;
        if (this.stats[i].value > this.maxStatValue) {
          this.maxStatValue = this.stats[i].value;
        }
      }
      this.revenue = this.group.cash;
      // TODO: the following order-dependent coupling is not ideal, but it currently seems
      //       to be the best way to do this given the way the data is currently organized.
      //       An ID-based coupling seems similarly dicey and a name-based coupling is not
      //       currently possible.
      this.customerMetric =
        this.group.metrics[0].value
          / (this.group.metrics[0].max != null ? this.group.metrics[0].max / 100 : 1);
      this.employeeMetric =
        this.group.metrics[1].value
          / (this.group.metrics[1].max != null ? this.group.metrics[1].max / 100 : 1);
      this.shareholderMetric =
        this.group.metrics[2].value
          / (this.group.metrics[2].max != null ? this.group.metrics[2].max / 100 : 1);
      this.literatureScore =
        this.group.players[0].metrics[4].value
          / (this.group.players[0].metrics[4].max != null
            ? this.group.players[0].metrics[4].max / 100 : 1);
    },
    async onClickDownload() {
      // PDF download works in all browsers except pre-chromium Edge
      // (fails at domtoimage.toPng, and no replacement seems viable, see above)
      // fix for Edge (for now): make the page printable, and open the print dialog automatically
      // (in response to domtoimage.toPng or any other part of the download throwing any error)
      // The user can print to PDF or actually print. Bonus is you can print in other browsers too.
      // Edge requires the user to manually set the Scale in the print dialog to 50% for it to fit.
      // Note that this is generally no longer a problem with the new chromium-based Edge.

      this.pdf = true;
      await Vue.nextTick();
      await new Promise((resolve, reject) => {
        if (this.$refs.footerLogo && this.$refs.footerLogo.complete) { // wait for logo to load
          resolve();
        } else if (this.$refs.footerLogo) {
          this.$refs.footerLogo.addEventListener('load', resolve);
          this.$refs.footerLogo.addEventListener('error', reject);
        } else {
          console.log('No footerLogo ref found');
          resolve();
        }
      });

      await domtoimage.toPng(this.$refs.report, { cacheBust: true })
        .then((dataUrl) => {
          const doc = new JsPDF('p', 'pt', 'a4');
          doc.addImage(dataUrl, 'PNG', 18, 44.4, 561, 757);

          // TODO: if no autodownload, no soft-lock (explained below), so soft-lock fix commented out
          doc.save('Entrepreneurship Readiness Report.pdf');
          // Before we download the PDF, we have to consider the possibility that the
          // user's settings are set to view the PDF in the browser first when downloading a PDF
          // (instead of actually downloading it). This can be true in many modern browsers.
          // This is a problem because if it opens in the same page, it destroys the Vue app state.
          // This ends up soft-locking the player such that they can't get past the report page.
          // This problem came to my attention when a Firefox update changed the PDF download behavior
          // to default to viewing it in browser (on the same page) when you initiate a download.
          // To fix this while minimally impacting anyone whose browser actually just downloads stuff
          // when a download is initiated, instead first open a new tab, then artificially initiate
          // the download with a bogus DOM hyperlink element that targets the bloburl output by JsPDF,
          // which either downloads the file or loads the blob viewer. If it doesn't load the
          // blob viewer, we autoclose the tab, leaving you back on the report page. If it does load
          // the blob viewer, the user is left in the blob viewer, where they can either:
          // - close it, returning to the original report tab without downloading, where they can
          //   hit print or refresh to save off the report another way, or continue on with the game.
          // - download/print it (which presents a system dialog, and does carry over the expected
          //   default name) (and then close it to return back to the game).
          // const win = window.open(`${window.location.origin}/downloadplayerreport/${encodeURIComponent('Entrepreneurship Readiness Report.pdf')}/${encodeURIComponent(doc.output('bloburl', { filename: 'Entrepreneurship Readiness Report.pdf' }))}`, '_blank');
          // if (win == null) {
          //   doc.save('Entrepreneurship Readiness Report.pdf');
          // }

          this.pdf = false;
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Failed to autodownload report PDF!', error);
          try {
            window.print();
            this.pdf = false;
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to open print dialog!', e);
            this.pdf = false;
          }
        });
    },
  },
  watch: {
    async narrative(newValue) {
      if (newValue) await this.reset();
    }
  },
  async mounted() {
    this.setStats();

    await this.show();
    await Vue.nextTick();

    // uncomment to enable autodownload (but re-add soft-lock fix above if you do)
    // await this.onClickDownload();

    this.showNextButton = true;
  }
};
</script>

<style lang="scss" scoped>
@media print {
  body, body * {
    visibility: hidden !important;
    overflow: visible !important;
  }
  #report-container, #report-container * {
    visibility: visible !important;
    overflow: visible !important;
  }
  #report-container {
    left: 8.75% !important;
    top: 0 !important;
    width: 82.5% !important;
    overflow: visible !important;
    z-index: 10 !important;
  }
}

.download-button {
  position: absolute;
  bottom: 210px;
  right: 0;
  width: 350px;
  font-size: 28px;
  line-height: 55px;
  margin-right: 45px;
  z-index: 100 !important;
  transition: opacity 0.5s ease-in-out;
}

hr {
  border-top: 1px solid #e9ecef;
}
.report {
  background-color: white;
  border: solid;
  border-width: 3.5px;
  border-color: var(--secondaryBorderColor);
  color: var(--primaryColor);

  color-adjust: exact;
  -webkit-print-color-adjust: exact;
}
.report-container {
  position: absolute;
  width: 1150px;
  height: 850px;
  top: 20px;
  left: 30px;
  z-index: 1;
  overflow: auto;
}
.logo {
  object-fit: contain;
  height: 90%;
  max-width: 40%;
  margin: 10px 0px 10px 16px;
  z-index: 10;
}
.footer-logo {
  object-fit: contain;
  max-height: 30px;
  max-width: 30%;
  margin-bottom: 15px;
  z-index: 10;
}

.report-flex {
  margin-top: 126px;
  width: 99.97%;
  height: 128px;
  display: flex;
  justify-content: space-between;
  background-color: #B5B1D4;
  color: white;
  position: relative;
  //box-shadow: inset 0px 0px 20px 0px rgba(219,212,219,1);
}
.hero-title {
  width: 20%;
  margin: 4px 16px;
  font-size: 27.6pt;
  font-family: Verdana, Arial, Helvetica, sans-serif;
}
.hero-graph {
  position: absolute;
  top: -90%;
  left: 32%;
}
.hero-values {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
}
.hero-value {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 0 50.4px 0 0;
}
.hero-value-label {
  color: white;
  font-size: 42pt;
  font-family: Verdana, Arial, Helvetica, sans-serif;
  font-weight: bold;
  line-height: 100%;
  margin: -3.2px 0 0 0;
  white-space: nowrap;
}
.hero-value-number {
  color: var(--primaryColor);
  font-size: 22pt;
  font-family: Verdana, Arial, Helvetica, sans-serif;
  font-weight: bold;
  margin: -8px 0 0 0;
  white-space: nowrap;
}

.hero-tip {
  width: 100%;
  height: 128px;
  position: relative;
  background-color: var(--secondaryBorderColor);
}
.hero-tip-margin {
  margin: 67.6px 0 28.8px 0;
}
.hero-tip-extra-margin {
  margin: 119.6px 0 28.8px 0;
}
.hero-tip-flex-container {
  position: absolute;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  align-items: flex-end;
}
.hero-tip-details-flex-container {
  flex: 6;
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  align-items: stretch;
}
.hero-tip-definition-box {
  flex: 1;
  max-width: 16.67%;
  min-width: 16.5%;
  border: solid;
  border-radius: 10px;
  border-color: var(--secondaryBorderColor);
  border-width: 1.5px;
  padding: 6px 9px 8px;
  margin: 0 12px;
}
.hero-tip-label {
  color: white;
  font-weight: 500;
  font-variant: small-caps;
  font-size: 22.5pt;
  font-family: Verdana, Arial, Helvetica, sans-serif;
  text-align: left;
  line-height: 90%;
  width: 100%;
  display: inline-block;
  white-space: nowrap;
}
.hero-tip-label::first-letter {
  font-size: 25pt;
  font-family: Verdana, Arial, Helvetica, sans-serif;
  text-shadow: -2px 2px var(--secondaryColor);
}
.hero-tip-definition {
  color: var(--primaryColor);
  font-weight: normal;
  font-size: 13pt;
  font-family: Verdana, Arial, Helvetica, sans-serif;
  text-align: left;
  line-height: 100%;
}
.hero-tip-subtitle {
  left: 19%;
  position: absolute;
  color: var(--secondaryBorderColor);
  font-weight: bold;
  font-size: 11.16pt;
  font-family: Verdana, Arial, Helvetica, sans-serif;
  margin: -9.6px 0 12.8px 0;
  white-space: nowrap;
  width: fit-content;
}
.hero-tip-details {
  background-color: #E3E3F0;
  color: #666666;
  font-weight: normal;
  font-size: 10.9pt;
  font-family: Verdana, Arial, Helvetica, sans-serif;
  line-height: 112%;
  text-align: justify;
  border: solid;
  border-radius: 10px;
  border-color: #D9CDA8;
  border-width: 2.5px;
  padding: 2.4px 8px 4.8px;
  margin: 0 8px 0 0;
}

.report-title {
  font-size: 34.5pt;
  font-family: Verdana, Arial, Helvetica, sans-serif;
  font-weight: bold;
  color: var(--primaryColor);
  border-bottom: solid 8px var(--secondaryColor);
  margin: 10px 16px;
  white-space: nowrap;
}
.report-header {
  text-transform: uppercase;
  text-align: left;
}

.accent-color {
  background-color: #D9CDA8;
  color: black;
}

.labelbox {
  width: 97%;
  height: 64px;
  border-radius: 5px;
  padding: 1.5px 1.5px;
  margin: 30px 16px;
}
.labelbox-flexcontainer {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
.labelbox-flexcontainer-margin {
  margin: 0 41.6px;
}
.label {
  position: relative;
  font-size: 16.8pt;
  font-family: Verdana, Arial, Helvetica, sans-serif;
  white-space: nowrap;
}
.label-icon {
  position: absolute;
  margin: 0 5.6px;
  height: auto;
}
.valuebox {
  margin: 0 40px;
  height: 54px;
  border: 1.5px solid var(--secondaryBorderColor);
  border-radius: 5px;
  padding: 1.6px 1.6px;
  background-color: white;
}
.valuebox-flexcontainer {
  margin: 0 1.6px;
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}
.value-item {
  font-size: 21pt;
  font-family: Verdana, Arial, Helvetica, sans-serif;
  color: var(--primaryColor);
  align-self: center;
  white-space: nowrap;
  width: fit-content;
}
.extra-top-margin {
  margin-top: 64px;
}
.metrics-values {
  margin-left: 140px;
}
.metric-flexcontainer {
  margin: 0 29.6px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.metric-text-label {
  margin: 2px 0 0 0;
  font-size: 10.8pt;
  font-family: Verdana, Arial, Helvetica, sans-serif;
  align-self: center;
  text-transform: uppercase;
  color: black;
  white-space: nowrap;
}
.metric-text-value {
  margin: -2px 0 2px;
  font-size: 19.2pt;
  font-family: Verdana, Arial, Helvetica, sans-serif;
  line-height: 100%;
  align-self: center;
  color: var(--primaryColor);
  white-space: nowrap;
}

.slideInDown {
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.19, 1, 0.22, 1); // out expo
}
.fadeOutUp {
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19); // in cubic
}
.narrator-notification {
  bottom: 205px;
}
</style>



// WEBPACK FOOTER //
// src/components/game/narrative/NarrativeEntrepreneurshipReadinessReport.vue